import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useContext,
  startTransition,
} from "react";
import Crunker from "crunker";
import { useQueries } from "@tanstack/react-query";
import { ActionButtonGroup, ActionButton, Text } from "@react-spectrum/s2";
import { useHover, usePress } from "@react-aria/interactions";
import { mergeProps } from "@react-aria/utils";
import { useVideo, useVideoTemplate } from "@app/hooks/videos";
import { useUserProfile } from "@app/hooks/useIMS";
import { trackVoiceoverEvent, AnalyticsEventNames } from "@app/utils/analytics";
import { fetchPresignURL } from "@app/services/s3";
import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { AudioPlayer, getFormattedTime } from "@app/components/AudioPlayer";
import { S3Video } from "@app/components/S3Video";
import { VisuallyHidden } from "@app/components/VisuallyHidden";
import {
  VoiceoverAsset,
  validateAudioDuration,
  getTemplatePlaceholderKeys,
  resolveVoiceoverTemplateText,
} from "./VoiceoverAsset";
import AlertDiamondIcon from "@react-spectrum/s2/icons/AlertDiamond";
import EditIcon from "@react-spectrum/s2/icons/Edit";
import CheckmarkIcon from "@react-spectrum/s2/icons/Checkmark";
import UndoIcon from "@react-spectrum/s2/icons/Undo";

import {
  style,
  iconStyle,
} from "@react-spectrum/s2/style" with { type: "macro" };

const crunker = new Crunker();

const TranscriptSegment = ({
  videoId,
  assetName,
  segment,
  isSelected,

  onPress,
  onDone,

  ref,
}) => {
  const userProfile = useUserProfile();
  const video = useVideo(videoId);
  const template = useVideoTemplate(video.data?.templateId);
  const { updateAssets } = useContext(VideoWizardContext);

  const templateAsset = template.data?.assets?.find(
    (a) => a.name === assetName,
  );
  const audioVariation = template.data?.audioVariations?.find(
    (v) => v.referenceKey === assetName,
  );

  const asset = video.data?.assets?.find((a) => a.name === assetName) ?? {
    ...templateAsset,
    ...audioVariation,
  };

  const selectedCampaign = video.data?.campaigns?.find(
    (item) => item.id === video.data?.selectedCampaignIds?.[0],
  );

  const defaultText =
    templateAsset?.text ?? audioVariation?.settings?.text ?? "";

  const hasTextParts = getTemplatePlaceholderKeys(defaultText).length > 0;

  const text = hasTextParts
    ? resolveVoiceoverTemplateText(asset?.text ?? defaultText, {
        parts: asset?.parts,
        campaign: selectedCampaign,
      })
    : (asset?.text ?? defaultText);

  const isModified = hasTextParts
    ? Object.entries(asset?.parts ?? {}).some(
        ([key, value]) => value !== selectedCampaign?.[key],
      )
    : text !== defaultText;

  const [isEditing, setIsEditing] = useState(false);

  const { hoverProps, isHovered } = useHover({});
  const { pressProps } = usePress({
    onPress: () => onPress?.({ segment }),
  });

  const handleReset = (defaultAsset) => {
    updateAssets.mutate({ assets: [defaultAsset] });
  };

  return (
    <div
      ref={ref}
      key={asset.name}
      {...mergeProps(hoverProps, pressProps)}
      className={style({
        transition: "colors",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        paddingX: 24,
        paddingY: 16,
        margin: 4,
        borderRadius: "default",
        boxShadow: { isHovered: "elevated", default: "none" },
        backgroundColor: {
          isHovered: "layer-1",
          default: "transparent",
        },
        outlineWidth: 2,
        outlineColor: {
          isInvalid: "notice-700",
          isEditing: "transparent",
          isSelected: {
            isSelected: "accent-700",
            isInvalid: "notice-900",
            isEditing: "accent-700",
          },
          default: "transparent",
        },
        outlineOffset: -2,
        outlineStyle: "solid",
        position: "relative",
      })({ isHovered, isInvalid: segment?.isInvalid, isEditing, isSelected })}
    >
      <div className={style({ font: "detail" })}>
        {`${getFormattedTime(segment?.start ?? 0)} - ${getFormattedTime(segment?.end ?? 0)} `}
      </div>

      {!isEditing && (
        <div
          className={style({
            font: "body-lg",
            whiteSpace: "pre-wrap",
            textWrap: "pretty",
            maxWidth: 720,
          })}
        >
          {text}
        </div>
      )}

      {!isEditing && segment?.isInvalid && (
        <div
          className={style({
            display: "flex",
            alignItems: "center",
            gap: 4,
            font: "detail",
            color: "notice-800",
            marginTop: 8,
          })}
        >
          <AlertDiamondIcon
            styles={iconStyle({
              color: "notice",
            })}
          />
          {segment?.errorMessage?.join(". ")}
        </div>
      )}

      <VisuallyHidden isHidden={!isEditing}>
        <div
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
        >
          <VoiceoverAsset
            videoId={videoId}
            assetName={assetName}
            selectedCampaign={selectedCampaign}
            segments={asset?.segments ?? []}
            editableScript={template}
          />
        </div>
      </VisuallyHidden>

      {(isEditing || isHovered) && <></>}
      <ActionButtonGroup
        staticColor="auto"
        size="S"
        styles={style({ position: "absolute", top: 12, insetEnd: 12 })}
      >
        {isEditing && isModified && (
          <ActionButton
            isQuiet
            isDisabled={updateAssets.isPending}
            onPress={() => handleReset({ ...asset, text: defaultText })}
            aria-label="Reset to default"
          >
            <UndoIcon />
            <Text>Reset to default</Text>
          </ActionButton>
        )}
        <ActionButton
          isQuiet={!isEditing}
          onPress={() => {
            const nextEditing = !isEditing;
            setIsEditing(nextEditing);
            if (nextEditing) {
              trackVoiceoverEvent(AnalyticsEventNames.VOICEOVER_FRAGMENT_EDIT, {
                user: { email: userProfile?.email },
                videoId,
                templateId: video.data?.templateId,
              });
            } else {
              onDone?.({ assetName });
            }
          }}
          aria-label={isEditing ? "Done" : "Edit"}
        >
          {isEditing ? <CheckmarkIcon /> : <EditIcon />}
          {isEditing && <Text>Done</Text>}
        </ActionButton>
      </ActionButtonGroup>
    </div>
  );
};

export const VoiceoverTimeline = ({ videoId, previewUrl }) => {
  const videoPlayerRef = useRef(null);
  const waveformRef = useRef(null);
  const transcriptsRef = useRef([]);

  const userProfile = useUserProfile();
  const video = useVideo(videoId);
  const template = useVideoTemplate(video.data?.templateId);
  const [audioRefreshNonce, setAudioRefreshNonce] = useState(0);
  const handleSegmentDone = useCallback(() => {
    setAudioRefreshNonce((nonce) => nonce + 1);
  }, []);

  const templateAssets =
    template.data?.assets?.filter((asset) => asset.type === "audio") ?? [];

  const audioAssets = useMemo(
    () =>
      templateAssets?.map((templateAsset) =>
        video.data?.assets?.find((asset) => asset.name === templateAsset.name),
      ) ?? [],
    [templateAssets, video.data?.assets],
  );

  const audioAssetsFingerprint = audioAssets
    .map((asset) => `${asset?.filePath ?? ""}:${asset?.generatedAt ?? ""}`)
    .join("|");

  const [...audioPresignQueries] = useQueries({
    queries: audioAssets.map((asset) => ({
      queryKey: [
        "audioAssetPresignURL",
        asset?.filePath,
        asset?.generatedAt ?? 0,
        audioRefreshNonce,
      ],
      queryFn: ({ signal }) => fetchPresignURL(asset.filePath, { signal }),
      enabled: !!asset?.filePath,
    })),
  });

  const [...audioBuffers] = useQueries({
    queries: audioAssets.map((asset, index) => {
      const generatedAt = asset?.generatedAt ?? 0;
      const presignURL = audioPresignQueries[index]?.data?.presignURL;
      return {
        queryKey: [
          "audioBuffer",
          asset?.filePath,
          generatedAt,
          audioRefreshNonce,
        ],
        queryFn: async ({ signal }) => {
          const response = await fetch(presignURL, {
            signal,
            cache: "no-store",
          });

          if (!response.ok) {
            throw new Error(
              `Failed to fetch voiceover audio (${response.status})`,
            );
          }
          const arrayBuffer = await response.arrayBuffer();
          return arrayBuffer;
        },
        enabled: !!presignURL,
        retry: false,
      };
    }),
  });

  const audioBuffersReadyKey = audioBuffers
    .map(
      (buffer) =>
        `${buffer.status}:${buffer.fetchStatus}:${buffer.dataUpdatedAt ?? 0}`,
    )
    .join("|");

  const [mergedAudioURL, setMergedAudioURL] = useState(null);
  const mergedAudioURLRef = useRef(null);
  const [segments, setSegments] = useState([]);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(null);
  const lastAudioAssetsFingerprintRef = useRef(null);

  useEffect(
    () => () => {
      if (mergedAudioURLRef.current) {
        URL.revokeObjectURL(mergedAudioURLRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (lastAudioAssetsFingerprintRef.current === audioAssetsFingerprint) {
      return;
    }

    const isFirstRun = lastAudioAssetsFingerprintRef.current === null;
    lastAudioAssetsFingerprintRef.current = audioAssetsFingerprint;

    if (isFirstRun) {
      return;
    }

    if (mergedAudioURLRef.current) {
      URL.revokeObjectURL(mergedAudioURLRef.current);
      mergedAudioURLRef.current = null;
    }

    setMergedAudioURL(null);
  }, [audioAssetsFingerprint]);

  useEffect(() => {
    startTransition(async () => {
      if (!audioBuffers?.length) {
        return;
      }

      if (audioBuffers.some((buffer) => buffer.isFetching)) {
        return;
      }

      try {
        const decodedBuffers = await Promise.all(
          audioBuffers.map(async (buffer) => {
            if (!buffer.data) {
              return null;
            }
            try {
              return await crunker.context.decodeAudioData(buffer.data.slice(0));
            } catch {
              return null;
            }
          }),
        );

        const _segments = templateAssets?.map((asset, index) => {
          const variation = template.data?.audioVariations?.find(
            (audioVariation) => audioVariation.referenceKey === asset.name,
          );

          const start = variation?.settings?.inset ?? 0;
          const end = start + (variation?.settings?.duration ?? 0);
          const buffer = decodedBuffers[index];

          if (!buffer) {
            if (!audioAssets[index]?.isRegenerated) {
              return {
                index,
                label: variation?.label,
                start,
                end,
                isInvalid: false,
                errorMessage: null,
              };
            }

            return {
              index,
              label: variation?.label,
              start,
              end,
              isInvalid: true,
              errorMessage: [
                "This voiceover segment hasn't been generated yet.",
                "Generate it to continue.",
              ],
            };
          }

          const { isInvalid, errorMessage } = validateAudioDuration(
            buffer.duration,
            asset?.maxDuration,
            asset?.minDuration,
          );

          return {
            index,
            label: variation?.label,
            start,
            end,
            isInvalid,
            errorMessage,
          };
        });

        setSegments(_segments ?? []);

        const paddedBuffers = (
          await Promise.all(
            decodedBuffers.map(async (buffer, index) => {
              if (!buffer) {
                return null;
              }
              const paddingStart = _segments?.[index]?.start ?? 0;
              return paddingStart
                ? crunker.padAudio(buffer, 0, paddingStart)
                : buffer;
            }),
          )
        ).filter(Boolean);

        if (mergedAudioURLRef.current) {
          URL.revokeObjectURL(mergedAudioURLRef.current);
          mergedAudioURLRef.current = null;
        }

        if (paddedBuffers.length === 0) {
          setMergedAudioURL(null);
          return;
        }

        const mergedAudioBuffer = await crunker.mergeAudio(paddedBuffers);
        const mergedAudio = await crunker.export(
          mergedAudioBuffer,
          "audio/wav",
        );

        mergedAudioURLRef.current = mergedAudio.url;

        setMergedAudioURL(mergedAudio.url);
      } catch (error) {
        console.warn("Error merging audio buffers:", error);
      }
    });
  }, [audioBuffersReadyKey, audioAssetsFingerprint]);

  const handleRegionPress = useCallback(({ segment, region, waveform }) => {
    // region.play(true);
    // waveform.play(region.start, region.end);
    // waveform.play(region.start);

    waveform.setTime(region.start);

    // transcriptsRef.current?.[segment.index]?.scrollIntoView({
    //   behavior: "smooth",
    //   block: "center",
    // });
  }, []);

  const handleRegionIn = useCallback(({ segment, region, waveform }) => {
    transcriptsRef.current?.[segment.index]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    setSelectedSegmentIndex(segment.index);
  }, []);

  const handleRegionOut = useCallback(({ segment, region, waveform }) => {
    setSelectedSegmentIndex((prev) => (prev === segment.index ? null : prev));
  }, []);

  const handleTranscriptSegmentPress = useCallback(({ segment }) => {
    waveformRef.current?.setTime(segment.start + 0.0001);
  }, []);

  return (
    <>
      <div
        className={style({
          display: "grid",
          gridTemplateAreas: ["preview transcript"],
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "minmax(0, 1fr)",
          gap: 24,
          // maxHeight: 640,
          size: "full",
        })}
      >
        <div
          className={style({
            gridArea: "preview",
            display: "flex",
            flexDirection: "column",
            gap: 24,
            height: "calc(100vh - 250px)",
            paddingY: 24,
            position: "sticky",
            top: "calc(332px)",
          })}
        >
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              gap: 8,
            })}
          >
            <div
              className={style({
                position: "relative",
                width: "full",
                borderRadius: "default",
                overflow: "hidden",
              })}
            >
              <S3Video
                ref={videoPlayerRef}
                filePath={previewUrl ?? undefined}
                muted
                className={style({
                  display: "block",
                  aspectRatio: "16 / 9",
                  width: "full",
                })}
              />
              <div
                className={style({
                  position: "absolute",
                  bottom: 0,
                  insetStart: 0,
                  insetEnd: 0,
                  paddingX: 12,
                  paddingY: 8,
                  backgroundColor: "black/60",
                  pointerEvents: "none",
                })}
              >
                <Text
                  styles={style({
                    font: "detail",
                  })}
                >
                  Sample video preview — visuals for reference only
                </Text>
              </div>
            </div>

            <div
              className={style({
                padding: 8,
                backgroundColor: "elevated",
                borderRadius: "default",
                boxShadow: "elevated",
              })}
            >
              <AudioPlayer
                key={mergedAudioURL ?? "empty"}
                ref={waveformRef}
                url={mergedAudioURL}
                segments={segments}
                videoRef={videoPlayerRef}
                onRegionPress={handleRegionPress}
                onRegionIn={handleRegionIn}
                // onRegionOut={handleRegionOut}
                onPreviewPlay={() =>
                  trackVoiceoverEvent(
                    AnalyticsEventNames.ENTIRE_AUDIO_PREVIEWED,
                    {
                      user: { email: userProfile?.email },
                      videoId: video.data?.id,
                      templateId: video.data?.templateId,
                    },
                  )
                }
              />
            </div>
          </div>
        </div>

        <div className={style({ gridArea: "transcript" })}>
          <div
            className={style({
              size: "full",
              overflowY: "auto",
              marginBottom: "60dvh",
            })}
          >
            {templateAssets?.map((asset, idx) => (
              <TranscriptSegment
                ref={(ref) => {
                  transcriptsRef.current[idx] = ref;
                }}
                key={asset.name}
                videoId={video.data?.id}
                assetName={asset.name}
                segment={segments?.[idx]}
                isSelected={selectedSegmentIndex === idx}
                onPress={handleTranscriptSegmentPress}
                onDone={handleSegmentDone}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
